import React, { memo } from 'react';
import { Pressable, Platform, StyleProp, View, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';

export type SearchBarBackButtonProps = {
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
};

const SearchBarBackButton = ({
  onPress,
  style,
  accessibilityLabel = 'Go back',
}: SearchBarBackButtonProps) => {
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const isNativeMobile = Platform.OS !== 'web' && !isTablet;
  const buttonSize = isTablet ? 40 : (isNativeMobile ? 36 : 32);
  const iconSize = isTablet ? 22 : (isNativeMobile ? 16 : 18);
  const borderRadius = buttonSize * (10 / 36);
  const innerSize = buttonSize * (24 / 36);
  const innerRadius = buttonSize * (6 / 36);

  return (
    <Pressable
      onPress={onPress}
      hitSlop={8}
      style={({ pressed }) => [
        {
          width: buttonSize,
          height: buttonSize,
          borderRadius,
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderWidth: 1,
          borderColor: theme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)',
          shadowColor: theme ? '#000' : '#fff',
          shadowOpacity: theme ? 0.1 : 0.2,
          shadowOffset: { width: 0, height: 2 },
          shadowRadius: 6,
          elevation: 2,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transform: [{ scale: pressed ? 0.9 : 1 }],
          ...(Platform.OS === 'web' && { backdropFilter: 'blur(8px)' } as any),
        },
        style,
      ]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      <View
        style={{
          width: innerSize,
          height: innerSize,
          borderRadius: innerRadius,
          backgroundColor: theme ? '#f8fafc' : '#334155',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Ionicons name="arrow-back" size={iconSize} color={theme ? 'black' : 'white'} />
      </View>
    </Pressable>
  );
};

export default memo(SearchBarBackButton);
